<template>
    <kiosk-layout>
        <div>
            <page-header>
                Brands

                <template v-if="props.registrationEnabled" v-slot:actions>
                    <div data-test="registration-actions">
                        <vx-button href="/kiosk/ten-dlc/brands/new" size="lg" data-test="register-brand">
                            Register Brand <font-awesome-icon widthAuto :icon="faPlus" />
                        </vx-button>
                    </div>
                </template>
            </page-header>

            <div class="flex flex-col gap-12 lg:flex-row">
                <div class="shrink-0 lg:w-60">
                    <kiosk-tcr-brands-sidebar v-model="fetchParams"></kiosk-tcr-brands-sidebar>
                </div>
                <div class="w-full">
                    <kiosk-ten-dlc-controls
                        v-model="fetchParams"
                        placeholder="Search brand ID, name or EIN"
                        @apply="applyFilters"
                        @reset="resetFilters"
                    ></kiosk-ten-dlc-controls>

                    <page-loading v-if="loading && !list.length"></page-loading>

                    <empty-state
                        v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                        @reset="resetFilters"
                    />

                    <empty-state v-else-if="!list.length"> No brands found. </empty-state>

                    <div v-if="list.length">
                        <kiosk-tcr-brands-table>
                            <kiosk-tcr-brand-row
                                v-for="brand in list"
                                :key="brand.brand_id"
                                :modelValue="brand"
                                :registrationEnabled="props.registrationEnabled"
                            ></kiosk-tcr-brand-row>
                        </kiosk-tcr-brands-table>

                        <div v-if="fetchParams.cursor" class="mt-8">
                            <vx-button
                                @click="loadData()"
                                color="muted-dark"
                                size="xl"
                                class="grow-0"
                                data-test="load-more-btn"
                                :loading="loading"
                            >
                                Load More
                                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faPlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { watch } from 'vue';
import EmptyState from '~/components/general/EmptyState.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTenDlcControls from '~/components/kiosk/kiosk-ten-dlc/KioskTenDlcControls.vue';
import KioskTcrBrandRow from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrBrandRow.vue';
import KioskTcrBrandsSidebar from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrBrandsSidebar.vue';
import KioskTcrBrandsTable from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrBrandsTable.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useQueryParams } from '~/composables/useQueryString';
import { adminTenDlcClient } from '~/services';
import type { TcrBrand, TcrListParams } from '~/services/adminTenDlcClient.types';
import { isEqual } from '~/utils/helpers';

const props = withDefaults(defineProps<{ registrationEnabled?: boolean }>(), { registrationEnabled: false });

// Every sidebar key must be present (as undefined) so isEqual() against the
// live fetchParams keeps working after the sidebar writes undefined values.
const defaultFilters = (): TcrListParams => ({
    query: '',
    source: undefined,
    verified: undefined,
    vetting_score_buckets: undefined,
    has_campaigns: undefined,
    has_phone_numbers: undefined,
    sort_field: 'brand_id',
    sort_dir: 'asc',
    per_page: 25,
    cursor: null,
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination<TcrBrand, TcrListParams>();

const listCancelKey = Symbol('kiosk-tcr-brands');

const urlFilters = useQueryParams<TcrListParams>();

const syncUrl = () => {
    Object.assign(urlFilters, {
        query: fetchParams.value.query || undefined,
        source: fetchParams.value.source,
        verified: fetchParams.value.verified,
        vetting_score_buckets: fetchParams.value.vetting_score_buckets,
        has_campaigns: fetchParams.value.has_campaigns,
        has_phone_numbers: fetchParams.value.has_phone_numbers,
    });
};

const loadData = () => {
    syncUrl();
    fetchRecords(adminTenDlcClient.getTcrBrands(fetchParams.value, { cancelKey: listCancelKey }));
};

// Seeded before the watcher below is registered so it does not fire a second request.
fetchParams.value = {
    ...defaultFilters(),
    // The query-string parser coerces a digits-only search to a number.
    query: undefined === urlFilters.query ? '' : String(urlFilters.query),
    source: urlFilters.source,
    verified: urlFilters.verified,
    vetting_score_buckets: urlFilters.vetting_score_buckets,
    has_campaigns: urlFilters.has_campaigns,
    has_phone_numbers: urlFilters.has_phone_numbers,
};

// Deep-watching fetchParams would loop forever: fetchRecords writes the next
// cursor back into it after every response. Watch the sidebar filters by name.
watch(
    [
        () => fetchParams.value.source,
        () => fetchParams.value.verified,
        () => fetchParams.value.vetting_score_buckets,
        () => fetchParams.value.has_campaigns,
        () => fetchParams.value.has_phone_numbers,
    ],
    () => {
        fetchParams.value.cursor = null;
        loadData();
    }
);

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

loadData();
</script>
